import { SocketEvents } from '@sea-snag/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { useOrganization } from '../contexts/organization-context';
import { useAuth } from '../hooks/use-auth';
import { useSocket, useSocketEvent } from '../hooks/use-socket';
import { queryKeys } from '../lib/query-keys';
import type {
  CompSetSyncDonePayload,
  CompSetSyncFailedPayload,
  FirmSyncCompletedPayload,
  FirmSyncFailedPayload,
  SyncCompletedPayload,
  SyncFailedPayload,
} from '../lib/socket-client';
import { getOrganization, setOrganization } from '../stores/org-store';

/**
 * Component that manages socket connection based on auth status.
 * Automatically connects when user logs in and disconnects on logout.
 * Handles org room lifecycle and global sync event listeners.
 */
export function AuthSocketManager() {
  const { status: authStatus } = useAuth();
  const { organization } = useOrganization();
  const { connect, disconnect, socket, isConnected } = useSocket();
  const queryClient = useQueryClient();
  const wasAuthenticated = useRef(false);
  const previousOrgPid = useRef<string | null>(null);

  useEffect(() => {
    const isAuthenticated = authStatus === 'authenticated';

    if (isAuthenticated && !wasAuthenticated.current) {
      connect();
    } else if (!isAuthenticated && wasAuthenticated.current) {
      disconnect();
    }

    wasAuthenticated.current = isAuthenticated;
  }, [authStatus, connect, disconnect]);

  // Org room join/leave lifecycle
  useEffect(() => {
    if (!socket || !isConnected || !organization) return;

    const newOrgPid = organization.pid;

    if (previousOrgPid.current && previousOrgPid.current !== newOrgPid) {
      socket.emit('leave:org', { orgPid: previousOrgPid.current });
    }

    socket.emit('join:org', { orgPid: newOrgPid });
    previousOrgPid.current = newOrgPid;

    return () => {
      if (previousOrgPid.current) {
        socket.emit('leave:org', { orgPid: previousOrgPid.current });
        previousOrgPid.current = null;
      }
    };
  }, [socket, isConnected, organization]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Global sync event handlers
  const handleSyncCompleted = useCallback(
    (data: SyncCompletedPayload) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.boats.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.all });

      const { boatsCreated, boatsUpdated, boatsDeactivated } = data.result;
      const parts: string[] = [];
      if (boatsCreated > 0) parts.push(`${boatsCreated} new`);
      if (boatsUpdated > 0) parts.push(`${boatsUpdated} updated`);
      if (boatsDeactivated > 0) parts.push(`${boatsDeactivated} deactivated`);

      toast.success('Fleet synced', {
        description:
          parts.length > 0 ? `Boats: ${parts.join(', ')}` : 'Your fleet data is up to date.',
      });
    },
    [queryClient]
  );

  const handleSyncFailed = useCallback(
    (_data: SyncFailedPayload) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.all });

      toast.error('Sync failed', {
        description:
          'There was a problem syncing your fleet data. Check your integration settings.',
      });
    },
    [queryClient]
  );

  // Comp-set sync event handlers
  const handleCompSetSyncDone = useCallback(
    (data: CompSetSyncDonePayload) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.compSets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.compSets.detailAll });

      toast.success(`Competitor sync for "${data.compSetName}" is done`, {
        description: `Found ${data.marketBoatCount} ${data.marketBoatCount === 1 ? 'competitor' : 'competitors'}.`,
        action: {
          label: 'View',
          onClick: () => {
            const org = getOrganization();
            if (org) window.location.href = `/${org.pid}/competitor-sets/${data.compSetPid}`;
          },
        },
      });
    },
    [queryClient]
  );

  const handleCompSetSyncFailed = useCallback(
    (_data: CompSetSyncFailedPayload) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.compSets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.compSets.detailAll });

      toast.error('Competitor sync failed', {
        description: 'There was a problem syncing competitor boats. Please try again.',
      });
    },
    [queryClient]
  );

  // Firm sync event handlers
  const handleFirmSyncCompleted = useCallback(
    (_data: FirmSyncCompletedPayload) => {
      // Update org store so the onboarding gate closes immediately
      if (organization) {
        setOrganization({ ...organization, setupCompletedAt: new Date().toISOString() });
      }

      const orgPid = organization?.pid;
      if (orgPid) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.setup.status(orgPid) });
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.boats.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.all });

      toast.success('Firm sync completed', {
        description: 'Your fleet data, pricing, and availability are up to date.',
      });
    },
    [queryClient, organization]
  );

  const handleFirmSyncFailed = useCallback(
    (data: FirmSyncFailedPayload) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.all });

      toast.error('Firm sync failed', {
        description: `Sync failed during ${data.phase} phase. ${data.retryable ? 'It will be retried automatically.' : 'Check your integration settings.'}`,
      });
    },
    [queryClient]
  );

  const handleNotificationNew = useCallback(
    (data: { id: string; type: string; title: string; message: string }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });

      toast.info(data.title, {
        description: data.message,
        action: {
          label: 'View',
          onClick: () => {
            const org = getOrganization();
            if (org) window.location.href = `/${org.pid}/activity`;
          },
        },
      });
    },
    [queryClient]
  );

  useSocketEvent(SocketEvents.SYNC_COMPLETED, handleSyncCompleted);
  useSocketEvent(SocketEvents.SYNC_FAILED, handleSyncFailed);
  useSocketEvent(SocketEvents.COMP_SET_SYNC_DONE, handleCompSetSyncDone);
  useSocketEvent(SocketEvents.COMP_SET_SYNC_FAILED, handleCompSetSyncFailed);
  useSocketEvent(SocketEvents.FIRM_SYNC_COMPLETED, handleFirmSyncCompleted);
  useSocketEvent(SocketEvents.FIRM_SYNC_FAILED, handleFirmSyncFailed);
  useSocketEvent(SocketEvents.NOTIFICATION_NEW, handleNotificationNew);

  return null;
}
